import { CvpLocale } from "../constants/CvpLocale";
import { useSettings } from "../settings/useSettings";
import { CVPSearchParams, queryStringStore } from "../url/wsQueryStringStore";
import { useWhoAmI } from "../user/useWhoAmI";
import { lp } from "../utils/logPrefix";
import { getStorageArea } from "../utils/storageArea";
import { validateLocale } from "./validateLocale";

const storageArea = getStorageArea("session");

/**
 *
 * This is the only place where locale is determined
 *
 * LOCALE PRIORITY:
 * 1. settings?.iframe_overrideLocale
 * 2. locale fra querystring (f.eks &cvplocale=en)
 * 3. locale fra auth/WhoAmI    (oppdatert med authAPI.saveLocaleChoice)
 * 4. locale fra sessionStorage.getItem("locale")
 * 5. locale fra settings (velges i /admin)
 * 6. fallback til nb
 *
 *
 * See also useSetLocale()
 *
 */
export function useEvaluatedLocale() {
  const settings = useSettings();
  const { whoAmI } = useWhoAmI();
  const localeFromQs =
    queryStringStore.qs[CVPSearchParams.cvplocale]?.toString();

  if (settings?.iframe_overrideLocale) {
    const isValid = validateLocale(settings?.iframe_overrideLocale);

    if (isValid) {
      return settings?.iframe_overrideLocale;
    }

    console.error(
      lp(),
      "Invalid locale from settings?.iframe_overrideLocale",
      settings?.iframe_overrideLocale
    );
  }

  // locale fra querystring (f.eks &cvplocale=en)
  if (localeFromQs) {
    const isValid = validateLocale(localeFromQs);

    if (isValid) {
      return localeFromQs as CvpLocale;
    }

    console.error(lp(), "Invalid locale from query string", localeFromQs);
  }

  // locale fra auth/WhoAmI    (oppdatert med authAPI.saveLocaleChoice)
  if (whoAmI.locale) {
    const isValid = validateLocale(whoAmI.locale);

    if (isValid) {
      return whoAmI.locale as CvpLocale;
    }

    console.error(lp(), "Invalid locale from WhoAmI", whoAmI.locale);
  }

  // locale fra sessionStorage.getItem("locale")
  if (storageArea.getItem("locale")) {
    const isValid = validateLocale(storageArea.getItem("locale") as string);

    if (isValid) {
      return storageArea.getItem("locale") as CvpLocale;
    }

    console.error(
      lp(),
      "Invalid locale from sessionStorage",
      storageArea.getItem("locale")
    );
  }

  // locale fra settings (velges i /admin)
  if (settings?.locale) {
    const isValid = validateLocale(settings?.locale);

    if (isValid) {
      return settings?.locale;
    }

    console.error(
      lp(),
      "Invalid locale from settings?.locale",
      settings?.locale
    );
  }

  // fallback til nb
  return CvpLocale.nb;
}
